import React, { lazy, memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { FiltersSidebar, Button } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, number } from 'prop-types'

import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import { setDatePublished } from 'Pods/SearchPage/Store/actions/datePublished'
import { DATE_PUBLISHED } from 'Pods/SearchPage/Store/constants'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'

import { datePublishedFiltersConfig } from './constants'

// Keeps react-day-picker out of the search pack. Safe to suspend: both mount paths (the
// FiltersWrapper dropdown and MoreFiltersDrawer) render their content only after a user
// interaction, so this boundary is never reached during the page's SSR pass.
const CustomRange = lazy(
  () => import(/* webpackChunkName: "search-custom-date-range" */ './Components/CustomRange/CustomRange')
)

function DatePublishedContent({ limit = 0, hideShowMore = true }) {
  const translate = useTranslate()

  const datePublished = useSearchPageStore(state => state.datePublished ?? DATE_PUBLISHED.ALL_TIME)
  // The drawer unmounts this on close, so a preset past `limit` would come back hidden behind
  // "Show more" with nothing saying it is on. Start expanded when one is, and only on mount -
  // re-deriving would refuse to collapse while such a preset is applied.
  const [showAll, setShowAll] = useState(() => !hideShowMore && datePublishedFiltersConfig[0].filters
    .slice(limit)
    .some(({ value }) => value === datePublished))
  // limit=0 tells FiltersGroup to show all items (0 || initFilters.length = all)
  const effectiveLimit = !hideShowMore && showAll ? 0 : limit

  // Selecting Custom replaces the preset before any range is applied; remember it so cancelling
  // the calendar does not leave an empty `custom` behind. Tracked off the store rather than off
  // the change handler because external writes (Clear all, the Reset button, popstate restore)
  // never pass through it, and a stale ref would resurrect a preset the user already dropped.
  const previousDatePublished = useRef(DATE_PUBLISHED.ALL_TIME)

  useEffect(() => {
    if (datePublished !== DATE_PUBLISHED.CUSTOM) {
      previousDatePublished.current = datePublished
    }
  }, [datePublished])

  const handleFilterChange = useCallback((_, value) => {
    setDatePublished(value)
  }, [])

  const handleCustomRangeCancel = useCallback(() => {
    setDatePublished(previousDatePublished.current)
  }, [])

  const selectedFilters = useMemo(() => [datePublished], [datePublished])

  const filters = useMemo(() => datePublishedFiltersConfig.map((group) => ({
    ...group,
    filters: group.filters.map(({ value, labelKey }) => ({
      value,
      label: translate(`search_page.filters.date_published.${labelKey}`),
    })),
  })), [])

  return (
    <>
      <FiltersSidebar
        isCollapsible={false}
        filters={filters}
        selectedFilters={selectedFilters}
        onFiltersChange={handleFilterChange}
        limit={effectiveLimit}
        useRadio
        hideLastDivider
        hideShowMore
        baseFormWrapperClassName="filter-wrapper__content"
      />
      {datePublished === DATE_PUBLISHED.CUSTOM && (
        <Suspense fallback={null}>
          <CustomRange onCancel={handleCustomRangeCancel} />
        </Suspense>
      )}
      {!hideShowMore && (
        <Button
          type="ghost-primary"
          size="small"
          customClass="filter-wrapper__show-more cgt-link-primary"
          onClick={() => setShowAll(value => !value)}
        >
          {translate(`search_page.filters.date_published.${showAll ? 'show_less' : 'show_more'}`)}
        </Button>
      )}
    </>
  )
}

DatePublishedContent.propTypes = {
  limit: number,
  hideShowMore: bool,
}

export default memo(DatePublishedContent)
